import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";

const CHANNELS = ["Paid Search", "TV", "Billboard", "Directory", "Lead Purchase", "Radio", "Social", "Referral", "Organic"];

const empty = {
  practice_area: "",
  office: "",
  channel: "Paid Search",
  campaign: "",
  vendor_id: "none",
  year: 2026,
  budgeted: 0,
  contracted: 0,
};

export default function Budgets() {
  const [budgets, setBudgets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const { user } = useAuth();
  const writable = can(user?.role, "budget:write");

  const load = () => {
    api.get("/budgets").then((r) => setBudgets(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.practice_area || !form.office) return toast.error("Practice area and office are required");
    await api.post("/budgets", {
      ...form,
      vendor_id: form.vendor_id === "none" ? null : form.vendor_id,
      year: Number(form.year),
      budgeted: Number(form.budgeted) || 0,
      contracted: Number(form.contracted) || 0,
    });
    toast.success("Budget line added");
    setOpen(false);
    setForm(empty);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/budgets/${id}`);
    load();
  };

  const totals = budgets.reduce(
    (a, b) => ({ budgeted: a.budgeted + (b.budgeted || 0), contracted: a.contracted + (b.contracted || 0) }),
    { budgeted: 0, contracted: 0 }
  );
  const vName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Planning</p>
          <h1 className="font-display text-4xl font-light mt-1">Marketing Budget Center</h1>
        </div>
        {writable && (<Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B1F33] text-white rounded-none hover:bg-zinc-800" data-testid="add-budget-button">
              <Plus className="h-4 w-4 mr-1" /> Add budget line
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">New budget line</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>Practice area</Label>
                <Input value={form.practice_area} onChange={upd("practice_area")} data-testid="budget-area-input" className="mt-1 rounded-none" />
              </div>
              <div>
                <Label>Office</Label>
                <Input value={form.office} onChange={upd("office")} data-testid="budget-office-input" className="mt-1 rounded-none" />
              </div>
              <div>
                <Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="budget-channel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHANNELS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Campaign</Label>
                <Input value={form.campaign} onChange={upd("campaign")} className="mt-1 rounded-none" />
              </div>
              <div className="col-span-2">
                <Label>Vendor</Label>
                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="budget-vendor-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Budgeted ($)</Label>
                <Input type="number" value={form.budgeted} onChange={upd("budgeted")} data-testid="budget-amount-input" className="mt-1 rounded-none" />
              </div>
              <div>
                <Label>Contracted ($)</Label>
                <Input type="number" value={form.contracted} onChange={upd("contracted")} className="mt-1 rounded-none" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-budget-button">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>)}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
        <div className="border border-zinc-200 bg-white p-4">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">Total budgeted</p>
          <p className="font-display text-3xl mt-1">{currency(totals.budgeted)}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">Total contracted</p>
          <p className={`font-display text-3xl mt-1 ${totals.contracted > totals.budgeted ? "text-[#B54747]" : ""}`}>
            {currency(totals.contracted)}
          </p>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Practice area</TableHead>
              <TableHead>Office</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead className="text-right">Budgeted</TableHead>
              <TableHead className="text-right">Contracted</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {budgets.map((b) => (
              <TableRow key={b.id} className="hover:bg-zinc-50" data-testid={`budget-row-${b.id}`}>
                <TableCell className="font-medium">{b.practice_area}</TableCell>
                <TableCell>{b.office}</TableCell>
                <TableCell>{b.channel}</TableCell>
                <TableCell className="text-zinc-500">{vName(b.vendor_id)}</TableCell>
                <TableCell className="text-right font-mono-data">{currency(b.budgeted)}</TableCell>
                <TableCell className="text-right font-mono-data">{currency(b.contracted)}</TableCell>
                <TableCell>
                  {writable && (
                  <button onClick={() => remove(b.id)} className="text-zinc-300 hover:text-[#B54747]" data-testid={`delete-budget-${b.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {budgets.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-zinc-500 py-10">
                  No budget lines yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
